import type { FC } from 'react';
import { Button } from '@/components/ui/button';
import { Badge, type BadgeTone } from '@/components/ui/badge';
import { Callout } from '@/components/ui/callout';
import { Card } from '@/components/ui/card/Card';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/hooks/useTranslation';
import type { Process } from '../type/Process';

const TOTAL_MAPPABLE_FIELDS = 18;

function countPrefilledFields(process: Process): number {
  const candidates: (string | number | null)[] = [
    process.operator,
    process.operation,
    process.ae,
    process.ap,
    process.vc,
    process.fz,
    process.machinedVolume,
    process.machinedDistance,
    process.machinedPieces,
    process.totalMachiningDuration,
    process.pieceMachiningDuration,
    process.malround,
    process.lubType,
    process.lubBrand,
    process.holderType,
    process.holderBrand,
    process.clampingType,
    process.clampingBrand,
  ];

  let count = candidates.filter((v) => v !== null).length;

  if (process.machineIds.length > 0) {
    count += 1;
  }

  return Math.min(count, TOTAL_MAPPABLE_FIELDS);
}

interface ProcessSelectionCardProps {
  processes: Process[];
  isLoading: boolean;
  error: string | null;
  onSelect: (p: Process) => void;
  onSkip: () => void;
}

interface ColumnHeaderProps {
  label: string;
  count: number;
  tone: Extract<BadgeTone, 'success' | 'warning'>;
}

const COLUMN_BOX: Record<ColumnHeaderProps['tone'], string> = {
  success: 'border-success/30 bg-success/10 text-success',
  warning: 'border-warning/30 bg-warning/10 text-warning',
};

const ColumnHeader: FC<ColumnHeaderProps> = ({ label, count, tone }) => (
  <div
    className={cn(
      'flex items-center justify-between rounded-xl border px-3 py-1.5',
      COLUMN_BOX[tone]
    )}
  >
    <span className="text-xs font-semibold tracking-wide uppercase">{label}</span>
    <Badge tone={tone}>{count}</Badge>
  </div>
);

export const ProcessSelectionCard: FC<ProcessSelectionCardProps> = ({
  processes,
  isLoading,
  error,
  onSelect,
  onSkip,
}) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <Skeleton
        className="h-20 rounded-xl"
        aria-label={t('traceability.processSelection.loading')}
      />
    );
  }

  if (error) {
    return (
      <Callout
        tone="error"
        title={t('traceability.processSelection.errorTitle')}
        action={
          <Button type="button" variant="default" size="sm" onClick={onSkip}>
            {t('traceability.processSelection.skip')}
          </Button>
        }
      >
        {t('traceability.processSelection.errorDescription')}
      </Callout>
    );
  }

  const standardProcesses = processes.filter((p) => p.processType === 'standard');
  const testProcesses = processes.filter((p) => p.processType === 'test');

  const renderProcessButton = (process: Process, idx: number) => {
    const prefilled = countPrefilledFields(process);
    const youFill = TOTAL_MAPPABLE_FIELDS - prefilled;
    return (
      <button
        key={process.id ?? idx}
        type="button"
        onClick={() => onSelect(process)}
        className="flex flex-col gap-1 w-full rounded-xl border border-border bg-muted/30 px-4 py-3 text-left text-sm transition-colors outline-none hover:bg-muted/60 focus-visible:ring-[3px] focus-visible:ring-ring/50"
      >
        <span className="font-semibold text-foreground">{process.name}</span>
        <div className="flex gap-2 flex-wrap">
          <Badge>{t('traceability.processSelection.prefilled', { count: prefilled })}</Badge>
          <Badge>{t('traceability.processSelection.youFill', { count: youFill })}</Badge>
        </div>
      </button>
    );
  };

  const renderEmptyPlaceholder = () => (
    <EmptyState size="compact" title={t('traceability.processSelection.emptyColumn')} />
  );

  return (
    <Card className="flex flex-col gap-3">
      <p className="text-sm font-semibold text-foreground">
        {t('traceability.processSelection.title')}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Standard column */}
        <div className="flex flex-col gap-2">
          <ColumnHeader
            label={t('traceability.processSelection.type.standard')}
            count={standardProcesses.length}
            tone="success"
          />
          {standardProcesses.length > 0
            ? standardProcesses.map((p, idx) => renderProcessButton(p, idx))
            : renderEmptyPlaceholder()}
        </div>

        {/* Test column */}
        <div className="flex flex-col gap-2">
          <ColumnHeader
            label={t('traceability.processSelection.type.test')}
            count={testProcesses.length}
            tone="warning"
          />
          {testProcesses.length > 0
            ? testProcesses.map((p, idx) => renderProcessButton(p, idx))
            : renderEmptyPlaceholder()}
        </div>
      </div>

      <Button type="button" variant="outline" size="sm" className="self-start" onClick={onSkip}>
        {t('traceability.processSelection.skip')}
      </Button>
    </Card>
  );
};
